<template>
  <v-card>
    <div class="person__info__pre-header">
      <div class="d-flex align-center pa-2">
        <v-btn class="secondary--text mr-3" @click="goBack" icon>
          <v-icon> mdi-arrow-left </v-icon>
        </v-btn>
        <v-divider vertical></v-divider>
        <h4 class="secondary--text font-weight-bold ml-6">
          {{ $t('fleet_management.persons.person_info.labels.card_title') }}
        </h4>
        <v-spacer></v-spacer>
        <div
          v-if="(!editable && isAdmin) || createdBy === loggedUser"
          class="edit-button"
        >
          <v-btn @click="toggleEditable" color="secondary" text>
            <SvgIcon
              @click="toggleEditable"
              class="tertiary--text"
              iconType="icon_edit_pen"
            />
            <span class="text-capitalize">{{
              $t('fleet_management.persons.person_info.labels.card_edit_button')
            }}</span>
          </v-btn>
        </div>
      </div>
    </div>
    <v-skeleton-loader
      v-if="loadingPerson"
      type="article"
      class="container"
    ></v-skeleton-loader>
    <v-form v-else ref="form" v-model="valid">
      <v-container>
        <v-row>
          <v-col cols="12">
            <div v-if="!editable">
              <PersonalData
                :person-info="{
                  personFirstName,
                  personLastName,
                  person,
                  personIdentificationType,
                  personIdentificationNumber
                }"
              />
            </div>
            <div v-else>
              <PersonalDataInput
                :isNewPersonView="isNewPersonView"
                :hasRoleContactEmergency="isContactEmergency"
                :personalDataProp="{
                  personFirstName,
                  personLastName,
                  person,
                  personIdentificationType,
                  personIdentificationNumber,
                  errorMessage,
                  idNumberIsRequiredMessage,
                  identificationTypeIsRequiredMessage,
                  showMessageTypeInput,
                  showMessageNumberInput
                }"
                @personalDataDialogEmit="handlePersonalDataDialogUpdate"
                @personalDataEditEmit="handlePersonalDataEditUpdate"
                ><div class="tertiary--text text-body-2">
                  {{
                    $t(
                      'fleet_management.persons.person_info.labels.card_field_mail'
                    )
                  }}
                </div>
                <span
                  class="value secondary--text d-flex flex-column"
                  :key="index"
                  v-for="(email, index) in person.emails"
                >
                  {{ email.email }}</span
                ></PersonalDataInput
              >
            </div>
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="12">
            <hr class="divider-dotted" />
          </v-col>
        </v-row>

        <v-row v-if="personRol.length > 0">
          <v-col>
            <v-row
              dense
              class="mb-3"
              :key="index"
              v-for="(rol, index) in personRol"
            >
              <v-col
                class="mt-0"
                cols="12"
                :key="index"
                sm="12"
                md="4"
                lg="3"
                xl="2"
              >
                <div
                  class="tertiary--text text-body-2"
                  v-if="editable || isAuthorizedToEdit()"
                >
                  {{
                    $t(
                      'fleet_management.persons.person_info.labels.card_field_rol'
                    )
                  }}
                </div>
                <v-select
                  v-if="isAuthorizedToEdit() && editable && isUserAdmin"
                  color="tertiary"
                  v-model="userRole[rol]"
                  :items="roleToChoose"
                  @change="changeSetRoles($event, index)"
                  :rules="[
                    v =>
                      !!v ||
                      $t(
                        'fleet_management.persons.new_person.rules.select_required'
                      )
                  ]"
                  dense
                  filled
                ></v-select>
                <span class="value secondary--text" v-else>
                  {{
                    $t(
                      `fleet_management.persons.person_info.labels.card_field_rol_${rol}`
                    )
                  }}
                </span>
              </v-col>

              <v-col cols="auto" v-if="!hideFieldByRol(rol)">
                <div class="tertiary--text text-body-2">
                  {{
                    $t(
                      'fleet_management.persons.person_info.labels.card_field_username'
                    )
                  }}
                </div>
                <span class="value secondary--text"> {{ email }}</span>
              </v-col>

              <v-col cols="auto" v-if="!hideFieldByRol(rol)">
                <div
                  class="tertiary--text text-body-2"
                  v-if="editable || isAuthorizedToEdit()"
                >
                  {{
                    $t(
                      'fleet_management.persons.person_info.labels.card_field_profile'
                    )
                  }}
                </div>
                <v-select
                  v-if="isAuthorizedToEdit() && editable && isUserAdmin"
                  v-model="profile"
                  :items="profiles"
                  @change="changeTheProfile"
                  item-text="text"
                  item-value="id"
                  :rules="[
                    v =>
                      !!v ||
                      $t(
                        'fleet_management.persons.new_person.rules.select_required'
                      )
                  ]"
                  dense
                  filled
                >
                  <template v-slot:item="data">
                    <span
                      :id="
                        data.item.text === 'Monitoreo'
                          ? 'idPersonasMonitoreo'
                          : data.item.text === 'Administrador de Subflota'
                          ? 'idPersonasAdsf'
                          : data.item.text === 'Administrador'
                          ? 'idPersonasAdmin'
                          : ''
                      "
                    >
                      {{ data.item.text }}
                    </span>
                  </template>
                </v-select>

                <span v-else class="value secondary--text d-flex flex-column">
                  {{ getProfile(rol) }}
                </span>
              </v-col>

              <template v-for="(contact, i) in personPhones">
                <v-col
                  :key="i + '_phone_type'"
                  cols="12"
                  sm="12"
                  md="4"
                  :lg="!editable ? 1 : 3"
                  xl="2"
                  :offset-sm="i > 0 ? '0' : '0'"
                  :offset-xl="i > 0 ? '2' : '0'"
                  :offset-md="i > 0 ? '3' : '0'"
                >
                  <div
                    class="tertiary--text text-body-2"
                    v-if="editable && rol == 'emergency_contact'"
                  >
                    {{ labels.phoneType }}
                  </div>
                  <v-select
                    v-if="editable && rol == 'emergency_contact'"
                    v-model="contact.phone.tag"
                    :items="types"
                    item-text="text"
                    item-value="type"
                    :rules="[
                      v =>
                        !!v ||
                        $t(
                          'fleet_management.persons.new_person.rules.select_required'
                        )
                    ]"
                    dense
                    filled
                    color="tertiary"
                  ></v-select>
                  <div v-else-if="rol == 'emergency_contact'">
                    <div class="tertiary--text text-body-2">
                      {{ labels.phoneType }}
                    </div>
                    <span class="value secondary--text d-flex flex-column">
                      {{ getType(contact.phone.tag) }}
                    </span>
                  </div>
                </v-col>

                <v-col
                  v-if="rol == 'emergency_contact'"
                  :key="i + '_phone_number'"
                  cols="12"
                  sm="12"
                  md="4"
                  lg="3"
                  xl="2"
                >
                  <InputNumberPhone
                    :phone="contact.phone"
                    :index="i"
                    :editable="editable"
                  />
                </v-col>
                <v-col
                  :key="i + '_remove_phone'"
                  cols="12"
                  sm="12"
                  md="3"
                  lg="2"
                  xl="2"
                  class="d-flex align-center justify-sm-center justify-md-start flex-nowrap"
                >
                  <FmButton
                    v-if="
                      personPhones.length > 2 &&
                        rol == 'emergency_contact' &&
                        editable
                    "
                    :type="'close'"
                    :button-text="'Eliminar'"
                    @click="removeContactEmergency(i)"
                  />
                </v-col>
                <v-col
                  cols="9 offset-3"
                  :key="i + '_add_number_contact'"
                  class="d-flex justify-start"
                >
                  <FmButton
                    v-if="
                      editable &&
                        i == personPhones.length - 1 &&
                        rol == 'emergency_contact' &&
                        i < 3
                    "
                    :button-text="
                      $t(
                        'fleet_management.persons.new_person.labels.add_number_of_contact'
                      )
                    "
                    :color="'tertiary'"
                    @click="addContactEmergency()"
                  />
                </v-col>
              </template>

              <v-col v-if="showThingsLoader" cols="12">
                <span class="tertiary--text text-body-2">
                  Entidades
                </span>
                <v-col cols="12">
                  <v-progress-circular
                    size="40"
                    indeterminate
                    color="primary"
                  ></v-progress-circular>
                </v-col>
              </v-col>

              <v-col v-else cols="12">
                <v-col
                  class="pl-0 py-1"
                  cols="7"
                  v-if="editable && isBusinessAccountUser(rol)"
                >
                  <span class="tertiary--text text-body-2">{{
                    getThingLimitLabel(item)
                  }}</span>
                </v-col>
                <v-col
                  class="pl-0 pt-1"
                  cols="6"
                  v-if="isBusinessAccountUser(rol)"
                >
                  <template
                    v-if="
                      !editable &&
                        (selectedThings.length === 0 || isUserAdmin) &&
                        (notDisplayDropdownThingsForAdsfAndMonitoring ||
                          isUserAdmin)
                    "
                  >
                    <span class="tertiary--text text-body-2">
                      Entidades
                    </span>
                  </template>

                  <template v-if="!editable && selectedThings.length">
                    <v-chip-group column>
                      <v-chip
                        v-for="item in selectedThingShowed"
                        :key="item"
                        class="mr-2"
                      >
                        {{ item.name }}
                      </v-chip>

                      <template
                        v-if="
                          selectedThingShowed.length !== selectedThings.length
                        "
                      >
                        <template
                          class="mt-2"
                          v-if="selectedThings.length >= 10"
                        >
                          <span> {{ textShowMore }} </span>
                          <FmButton
                            :button-text="'Ver más'"
                            @click="showMore"
                          ></FmButton>
                        </template>
                      </template>
                    </v-chip-group>
                  </template>

                  <template
                    v-else-if="
                      (isUserAdmin && editable) ||
                        notDisplayDropdownThingsForAdsfAndMonitoring
                    "
                  >
                    <v-autocomplete
                      v-model="selectedThings"
                      return-object
                      filled
                      dense
                      :items="
                        formatDataThings(filteredThingsByLiveTrackingService)
                      "
                      multiple
                      chips
                      deletable-chips
                      item-text="name"
                      no-data-text="no hay info"
                      color="tertiary"
                      :rules="isUserAdmin ? [] : [thingRules.required]"
                    >
                      <template
                        v-slot:prepend-item
                        v-if="
                          formatDataThings(filteredThingsByLiveTrackingService)
                            .length > 0
                        "
                      >
                        <v-list-item ripple @click="selectAll">
                          <v-list-item-action>
                            <v-icon>
                              {{ icon }}
                            </v-icon>
                          </v-list-item-action>
                          <v-list-item-content>
                            <v-list-item-title>
                              {{
                                $t(
                                  'fleet_management.persons.person_info.labels.form_title_select_all'
                                )
                              }}
                            </v-list-item-title>
                          </v-list-item-content>
                        </v-list-item>
                        <v-divider class="mt-2"></v-divider>
                      </template>
                      <template v-slot:selection="{ item, index }">
                        <v-chip v-if="index < 3">
                          <span>{{ item.name }}</span>
                        </v-chip>
                        <span
                          v-else-if="index === 3"
                          class="grey--text caption"
                        >
                          (+{{ selectedThings.length - 3 }})
                        </span>
                      </template>
                    </v-autocomplete>
                  </template>
                  <template v-if="selectedThings.length === 0 && !editable">
                    <div>
                      {{
                        $t(
                          'fleet_management.persons.person_info.alerts.no_entities'
                        )
                      }}
                    </div>
                  </template>
                </v-col>
              </v-col>

              <template v-if="rol === 'driver'">
                <v-col cols="12" sm="12" md="6" lg="4" xl="3">
                  <BaseDriver
                    :item="{ ibutton }"
                    @ibuttonEmit="value => (ibutton = value)"
                    :isEditable="editable"
                  />
                </v-col>
              </template>

              <div
                v-if="
                  selectedThingsEmergencyContact.length < 1 &&
                    !editable &&
                    selectedThings.length < 0
                "
                class="text-body-2 pb-2"
              >
                {{
                  $t(
                    'fleet_management.persons.person_info.labels.card_field_not_vehicles_configured'
                  )
                }}
              </div>

              <v-col
                cols="6"
                v-if="
                  (editable || selectedThingsEmergencyContact.length > 0) &&
                    rol == 'emergency_contact'
                "
              >
                <div class="text-body-2">
                  <div class="tertiary--text text-body-2 mb-2">
                    {{
                      $t(
                        'fleet_management.persons.person_info.labels.card_field_entity'
                      )
                    }}
                  </div>
                  <v-col
                    v-if="editable && loadingThingContactsThingList"
                    cols="12"
                  >
                    <v-progress-circular
                      size="40"
                      indeterminate
                      color="primary"
                    ></v-progress-circular>
                  </v-col>
                  <div class="mt-2 mb-2" v-if="!editable">
                    <template
                      v-for="(item, index) in selectedThingsEmergencyContact"
                    >
                      <v-chip
                        class="mr-2 mb-2"
                        v-if="index < thingsToShow"
                        :key="item"
                      >
                        {{ item.name }}
                      </v-chip>
                    </template>
                    <span
                      class="secondary--text mr-2"
                      v-if="
                        thingsToShow < selectedThingsEmergencyContact.length &&
                          thingsToShow <= 10
                      "
                    >
                      ( {{ selectedThingsEmergencyContact.length }} vehículos en
                      total )
                    </span>
                    <span
                      class="secondary--text mr-2"
                      v-if="
                        thingsToShow < selectedThingsEmergencyContact.length &&
                          thingsToShow > 10
                      "
                    >
                      ( +{{
                        selectedThingsEmergencyContact.length - thingsToShow
                      }}
                      )
                    </span>
                    <v-btn
                      small
                      color="primary"
                      text
                      v-on="$listeners"
                      v-if="
                        thingsToShow < selectedThingsEmergencyContact.length
                      "
                      @click="showMoreThingsContactEmergency"
                    >
                      <v-icon left>
                        mdi-plus-circle-outline
                      </v-icon>
                      <span class="text-capitalize text-body-2">
                        Ver más
                      </span>
                    </v-btn>
                  </div>
                  <v-autocomplete
                    v-if="editable"
                    v-model="selectedThingsEmergencyContact"
                    return-object
                    filled
                    dense
                    :items="formatDataThingsEmergencyContact"
                    multiple
                    chips
                    item-text="name"
                    no-data-text="no hay info"
                    color="tertiary"
                    :disabled="!editable"
                  >
                    <template v-slot:prepend-item v-if="true">
                      <v-list-item
                        ripple
                        @click="selectAllThingsContactEmergency"
                      >
                        <v-list-item-action>
                          <v-icon>
                            {{ iconContactEmergency }}
                          </v-icon>
                        </v-list-item-action>
                        <v-list-item-content>
                          <v-list-item-title>
                            {{
                              $t(
                                'fleet_management.persons.person_info.labels.form_title_select_all'
                              )
                            }}
                          </v-list-item-title>
                        </v-list-item-content>
                      </v-list-item>
                      <v-divider class="mt-2"></v-divider>
                    </template>
                    <template v-slot:selection="{ item, index }">
                      <v-chip v-if="index < 3">
                        <span>{{ item.name }}</span>
                      </v-chip>
                      <span v-else-if="index === 3" class="grey--text caption">
                        (+ {{ selectedThingsEmergencyContact.length - 3 }})
                      </span>
                    </template>
                  </v-autocomplete>

                  <div v-if="selectedThingsEmergencyContact.length >= 4">
                    <h4 class="red--text text-body-2 font-weight-normal">
                      {{
                        $t(
                          'fleet_management.persons.person_info.labels.card_field_priority_first_three_vehicles'
                        )
                      }}
                    </h4>
                  </div>
                </div>
              </v-col>

              <v-col class="d-flex justify-end" v-if="editable">
                <FmButton
                  v-show="personRol.length >= 2"
                  :button-text="'Eliminar Rol'"
                  @click="removeRol(index, rol)"
                  :type="'close'"
                />
              </v-col>

              <v-col cols="12">
                <hr class="divider-dotted" />
              </v-col>

              <v-col
                cols="12"
                class="d-flex justify-start"
                v-if="!hideButtonAddRole && editable"
              >
                <FmButton
                  v-show="personRol.length < 3"
                  :button-text="
                    $t('fleet_management.persons.new_person.labels.add_rol')
                  "
                  :color="'tertiary'"
                  @click="addRol(rol)"
                />
              </v-col>
            </v-row>
          </v-col>
        </v-row>

        <v-row v-else class="my-3">
          <v-col cols="auto">
            <div class="tertiary--text text-body-2" v-if="!editable">
              {{
                $t('fleet_management.persons.person_info.labels.card_field_rol')
              }}
            </div>
            <v-select
              v-if="editable && isUserAdmin"
              :label="labels.rol"
              color="tertiary"
              v-model="userRole['contact']"
              @input="changeTheRole($event)"
              :items="roleToChoose"
              :rules="[
                v =>
                  !!v ||
                  $t(
                    'fleet_management.persons.new_person.rules.select_required'
                  )
              ]"
              dense
              filled
            ></v-select>

            <span class="value secondary--text" v-else>
              {{
                $t(
                  `fleet_management.persons.person_info.labels.card_field_rol_contact`
                )
              }}</span
            >
          </v-col>
        </v-row>

        <v-row
          justify="space-between"
          class="footer"
          :style="{ visibility: editable ? 'visible' : 'hidden' }"
        >
          <v-col cols="auto">
            <v-btn @click="cancel" outlined="true" color="secondary" rounded>{{
              $t(
                'fleet_management.persons.person_info.labels.card_edit_person_cancel_button'
              )
            }}</v-btn>
          </v-col>
          <v-col cols="auto">
            <v-btn
              class="ml-2"
              color="tertiary white--text"
              @click="confirmUpdate"
              :disabled="!valid || formHasNotChanged || personDuplicated"
              rounded
              >{{
                $t(
                  'fleet_management.persons.person_info.labels.card_edit_person_save_button'
                )
              }}
            </v-btn>
          </v-col>
        </v-row>
      </v-container>
    </v-form>
    <v-snackbar v-model="snack" :color="snackColor" :timeout="snackBarTimeout">
      {{ snackText }}
      <template v-slot:action="{ attrs }">
        <v-btn v-bind="attrs" text @click="snack = false">
          {{
            $t(
              'fleet_management.persons.person_info.labels.card_snackbar_close_button'
            )
          }}
        </v-btn>
      </template>
    </v-snackbar>
    <v-dialog
      v-model="dialogName"
      persistent
      max-width="500px"
      transition="dialog-transition"
    >
      <v-card class="dialog-attention">
        <v-card-title class="orange--text">
          <SvgIcon class="orange--text" iconType="icon_attention" />
          {{ $t('fleet_management.persons.person_info.alerts.attention') }}
          <v-spacer></v-spacer>

          <v-btn icon @click="dialogName = false">
            <SvgIcon class="secondary--text" iconType="icon_close" />
          </v-btn>
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text class="secondary--text headline">{{
          $t('fleet_management.persons.person_info.errors.person_data_repeated')
        }}</v-card-text>
        <v-card-text class="secondary--text"
          >{{ repeatPersons.first_name }}
          {{ repeatPersons.last_name }}</v-card-text
        >
        <v-card-text class="tertiary--text">{{
          repeatPersons.emails[0].email
        }}</v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <Dialog
      v-if="showDialog"
      idCancelButton="idBtnPersonasCancelar"
      idSubmitButton="idBtnPersonasAceptar"
      @cancelButtonPressed="showDialog = false"
      :showDialog="showDialog"
      @submitButtonPressed="updatePerson"
      :loading="loadingThingContactsThingList"
      :text="dialog.text"
      type="warning"
    />
  </v-card>
</template>

<script>
import { SvgIcon, SNACKBAR_TIMEOUT } from '@leapsight/styleguide'
import store from '@/store'
import { mapState, mapGetters, mapActions } from 'vuex'
import PersonsHelpers from '@/helpers/persons/personsHelpers'
import ThingsHelpers from '@/helpers/things/thingsHelpers'
import { i18n } from '@leapsight/portal'
import Dialog from '@/components/dialog/Dialog'
import InputNumberPhone from '@/components/persons/contactEmergency/InputNumberPhone.vue'
import PersonalData from '@/components/persons/PersonalData.vue'
import PersonalDataInput from '@/components/persons/PersonalDataInput.vue'
import personsHelpers from '@/helpers/persons/personsHelpers'
import FmButton from '@/share/components/FmButton.vue'
import BaseDriver from '@/components/persons/Driver/BaseDriver.vue'

const ROL_USUARIO_ID = 1
const ROL_CONTACTO_ID = 2
const ROL_EMERGENCY_CONTACT_ID = 3
const ROL_DRIVER_ID = 4

const PROFILE_ADMINISTRADOR_SUBFLEET_ID = 3
const PROFILE_ADMINISTRADOR_ID = 2
const PROFILE_MONITOREO_ID = 1

const ROL_USER = 'user'
const ROL_EMERGENCY_CONTACT = 'emergency_contact'
const ROL_DRIVER = 'driver'
const ROL_CONTACT = 'contact'

export default {
  components: {
    BaseDriver,
    FmButton,
    PersonalData,
    PersonalDataInput,
    SvgIcon,
    Dialog,
    InputNumberPhone
  },
  data() {
    return {
      textShowMore: '',
      selectedThingShowed: [],
      showMessageTypeInput: false,
      showMessageNumberInput: false,
      identificationTypeIsRequiredMessage: '',
      idNumberIsRequiredMessage: '',
      thingsRemoved: [],
      thingsAdded: [],
      snackText: '',
      snackColor: '',
      originallySelectedThings: [],
      dataThingContactEmergencyAssignments: [],
      thingRules: {
        required: v =>
          Boolean(v.length) || 'Debe seleccionar al menos una entidad'
      },
      loadingPerson: true,
      loadingAssignedThings: true,
      loadingThings: false,
      loadingThingContactsThingList: false,
      selectedThings: [],
      selectedThingsEmergencyContact: [],
      copySelectedThingsEmergencyContact: [],
      allSelectedThingsFiltered: [],
      thingsToDelete: [],
      personDuplicated: false,
      errorMessage: '',
      hideButtonAddRole: false,
      identificationType: ['DNI', 'RUT', 'CI'],
      types: [
        { type: 'mobile', text: 'Movil' },
        { type: 'home', text: 'Fijo' },
        { type: 'work', text: 'Trabajo' },
        { type: 'other', text: 'Otro' }
      ],
      labels: {
        name: i18n.t(
          'fleet_management.persons.person_info.labels.card_field_name'
        ),
        lastName: i18n.t(
          'fleet_management.persons.person_info.labels.card_field_lastname'
        ),
        rol: i18n.t(
          'fleet_management.persons.person_info.labels.card_field_rol'
        ),
        profile: i18n.t(
          'fleet_management.persons.person_info.labels.card_field_profile'
        ),
        role: i18n.t(
          'fleet_management.persons.new_person.labels.card_field_rol'
        ),
        subProfile: i18n.t(
          'fleet_management.persons.new_person.labels.card_subtitle_profile'
        ),
        phoneType: 'Tipo'
      },
      profile: '',
      profiles: [
        {
          id: 1,
          text: i18n.t(
            'fleet_management.persons.new_person.labels.card_data_profile_monitor'
          )
        },
        {
          id: 3,
          text: i18n.t(
            'fleet_management.persons.new_person.labels.card_data_profile_fleet_admin'
          )
        },
        {
          id: 2,
          text: i18n.t(
            'fleet_management.persons.new_person.labels.card_data_profile_admin'
          )
        }
      ],
      userRole: {
        user: '',
        emergency_contact: '',
        driver: '',
        contact: ''
      },
      roleToChoose: [
        {
          value: 1,
          text: i18n.t(
            'fleet_management.persons.new_person.labels.card_data_role_user'
          ),
          disabled: false
        },
        {
          value: 3,
          text: 'Contacto de emergencia',
          disabled: false
        },
        {
          value: 4,
          text: 'Conductor',
          disabled: false
        },
        {
          value: 2,
          text: 'Contacto',
          disabled: false
        }
      ],
      dialog: {
        text: '',
        type: '',
        loading: false
      },
      dialogName: false,
      editable: false,
      inputDisable: true,
      personFirstName: '',
      personLastName: '',
      personIdentificationNumber: '',
      personIdentificationType: '',
      repeatPersons: {
        emails: [
          {
            email: ''
          }
        ]
      },
      personPhones: [],
      showDialog: false,
      snack: false,
      snackBarTimeout: SNACKBAR_TIMEOUT,
      valid: true,
      personProfile: '',
      originalThingsEmergencyContact: [],
      personRol: [],
      loggedUser: localStorage.getItem('user_id'),
      createdBy: null,
      rules: {
        contactIdNumber: v =>
          !!v ||
          i18n.t(
            'fleet_management.persons.new_person.rules.id_number_required'
          ),
        contactIdentificationType: v =>
          !!v ||
          i18n.t('fleet_management.persons.new_person.rules.select_required')
      },
      ibutton: '',
      personDataIsUpdated: false,
      thingsToShow: 10,
      allThingsFiltered: []
    }
  },
  methods: {
    ...mapActions('persons', ['getPersonsList']),
    showMoreThingsContactEmergency() {
      this.thingsToShow += 10
    },
    showMore() {
      const endIdx =
        this.selectedThingShowed.length + (10 % this.selectedThings.length)
      this.selectedThingShowed = structuredClone(this.selectedThings).splice(
        0,
        endIdx
      )
      this.textShowMore = `( +${this.selectedThings.length -
        this.selectedThingShowed.length} )`
    },
    getThingLimitLabel() {
      if (this.personProfile === 'business_account_user') {
        return i18n.t(
          'fleet_management.persons.persons.labels.title_limit_view_entities'
        )
      } else if (this.personProfile === 'business_account_fleet_admin') {
        return i18n.t(
          'fleet_management.persons.persons.labels.title_limit_view_entities_for_fleet'
        )
      } else {
        return ''
      }
    },
    handlePersonalDataDialogUpdate(data) {
      const { repeatPersons, dialogName } = data
      this.repeatPersons = repeatPersons
      this.dialogName = dialogName
    },
    handlePersonalDataEditUpdate(data) {
      const {
        personIdentificationType,
        personIdentificationNumber,
        personFirstName,
        personLastName
      } = data

      this.personFirstName = personFirstName
      this.personLastName = personLastName
      this.personIdentificationType = personIdentificationType
      this.personIdentificationNumber = personIdentificationNumber
    },
    onErrorMessageForContactEmergencyFields() {
      this.identificationTypeIsRequiredMessage = !this.personIdentificationType
        ? i18n.t(
            'fleet_management.persons.person_info.errors.type_document_required_c_e'
          )
        : ''
      this.idNumberIsRequiredMessage = !this.personIdentificationNumber
        ? i18n.t(
            'fleet_management.persons.person_info.errors.number_document_required'
          )
        : ''
      this.showMessageTypeInput = !this.personIdentificationType
      this.showMessageNumberInput = !this.personIdentificationNumber
    },
    offErrorMessageForContactEmergencyFields() {
      this.identificationTypeIsRequiredMessage = ''
      this.idNumberIsRequiredMessage = ''
      this.showMessageTypeInput = false
      this.showMessageNumberInput = false
    },
    onErrorIdentificationTypeRequired() {
      const hasPersonIdentifyType = Boolean(this.personIdentificationType)
      const hasPersonIdentifyNumber = Boolean(this.personIdentificationNumber)
      if (!hasPersonIdentifyType && hasPersonIdentifyNumber) {
        this.identificationTypeIsRequiredMessage = i18n.t(
          'fleet_management.persons.person_info.errors.type_document_required'
        )
        this.showMessageTypeInput =
          !hasPersonIdentifyType && hasPersonIdentifyNumber
      }
      return !hasPersonIdentifyType && hasPersonIdentifyNumber
    },
    removeRol(idx, rol) {
      this.offErrorMessageForContactEmergencyFields()
      this.personRol.splice(idx, 1)
      this.userRole[rol] = ''
      if (rol === ROL_EMERGENCY_CONTACT) {
        this.personPhones = []
      }
      if (rol === ROL_USER) {
        this.profile = ''
        this.selectedThings = []
      }
      this.initialRoleToChoseValue()
    },
    initialRoleToChoseValue() {
      this.roleToChoose.push({
        value: 2,
        text: 'Contacto',
        disabled: false
      })
      for (const role of this.roleToChoose) {
        role.value === this.userRole[ROL_EMERGENCY_CONTACT]
          ? (role.disabled = true)
          : role.value === this.userRole[ROL_DRIVER]
          ? (role.disabled = true)
          : role.value === this.userRole[ROL_USER]
          ? (role.disabled = true)
          : (role.disabled = false)
      }
    },
    addRol(rol) {
      if (rol === PersonsHelpers.ROL_ID.contact) {
        this.roleToChoose[1].disabled = true
        this.roleToChoose[0].disabled = true
        this.roleToChoose[2].disabled = true
      }

      if (this.roleToChoose.length > 3) {
        this.userRole[ROL_CONTACT] = ''
        this.roleToChoose.pop()
      }

      const initIdx = this.roleToChoose.findIndex(
        role =>
          !Object.values(this.userRole).includes(role.value) && !role.disabled
      )

      const roleId = this.roleToChoose[initIdx].value
      let rolName = ''

      for (const prop in PersonsHelpers.ROL_ID) {
        if (PersonsHelpers.ROL_ID[prop] === roleId) {
          rolName = prop === 'emergencyContact' ? ROL_EMERGENCY_CONTACT : prop
          break
        }
      }

      this.personRol.push(rolName)
      this.userRole[rolName] = roleId

      for (const role of this.roleToChoose) {
        role.value === this.userRole[ROL_EMERGENCY_CONTACT]
          ? (role.disabled = true)
          : role.value === this.userRole[ROL_DRIVER]
          ? (role.disabled = true)
          : role.value === this.userRole[ROL_USER]
          ? (role.disabled = true)
          : (role.disabled = false)
      }

      if (roleId === PersonsHelpers.ROL_ID.emergencyContact) {
        this.onErrorMessageForContactEmergencyFields()
        if (this.personPhones.length < 2) {
          this.addContactEmergency()
          this.addContactEmergency()
        }
      }

      if (roleId === PersonsHelpers.PROFILE_ID.user) {
        this.profile = ''
      }
    },
    getType(t) {
      return this.types.find(type => type.type == t).text
    },
    removeContactEmergency(i) {
      this.personPhones.splice(i, 1)
    },
    addContactEmergency() {
      this.personPhones.push({ phone: { number: '', tag: 'mobile' } })
    },
    hideFieldByRol(rol) {
      return rol !== ROL_USER
    },
    changeTheProfile(profile) {
      switch (profile) {
        case PROFILE_ADMINISTRADOR_ID:
          this.personProfile = 'business_account_admin'
          this.thingsAdded = []
          break
        case PROFILE_ADMINISTRADOR_SUBFLEET_ID:
          this.personProfile = 'business_account_fleet_admin'
          this.thingsAdded = []
          break
        case PROFILE_MONITOREO_ID:
          this.personProfile = 'business_account_user'
          this.selectedThings = structuredClone(this.originallySelectedThings)
          this.thingsRemoved = []
          this.thingsAdded = []
          break
      }
    },
    changeSetRoles(rol, idx) {
      if (rol === ROL_CONTACTO_ID) {
        this.offErrorMessageForContactEmergencyFields()
        this.hideButtonAddRole = true
        this.personRol = []
        this.personPhones = []
        this.userRole[ROL_USER] = ''
        this.userRole[ROL_EMERGENCY_CONTACT] = ''
        this.userRole[ROL_DRIVER] = ''
        this.userRole[ROL_CONTACT] = ROL_CONTACTO_ID
        this.roleToChoose.forEach(rtc => (rtc.disabled = false))
        return
      }

      if (rol !== ROL_CONTACTO_ID) this.hideButtonAddRole = false

      this.userRole[this.personRol[idx]] = ''

      let rolName = ''

      for (const prop in PersonsHelpers.ROL_ID) {
        if (PersonsHelpers.ROL_ID[prop] === rol) {
          rolName = prop === 'emergencyContact' ? ROL_EMERGENCY_CONTACT : prop
          break
        }
      }

      this.personRol[idx] = rolName
      this.userRole[this.personRol[idx]] = rol

      if (rol === ROL_EMERGENCY_CONTACT_ID) {
        this.onErrorMessageForContactEmergencyFields()
        if (this.personPhones.length < 2) {
          this.addContactEmergency()
          this.addContactEmergency()
        }
      }

      if (rol === ROL_USUARIO_ID) {
        if (!this.personRol.includes(ROL_EMERGENCY_CONTACT)) {
          this.personPhones = []
          this.offErrorMessageForContactEmergencyFields()
        }
        if (!Boolean(this.profile)) {
          this.selectedThings = []
          this.thingsAdded = []
          this.thingsRemoved = []
          this.profile = ''
        }
      }

      if (rol === ROL_DRIVER_ID) {
        if (!this.personRol.includes(ROL_EMERGENCY_CONTACT)) {
          this.personPhones = []
          this.offErrorMessageForContactEmergencyFields()
        }
        this.ibutton = ''
      }

      for (const role of this.roleToChoose) {
        role.value === this.userRole[ROL_EMERGENCY_CONTACT]
          ? (role.disabled = true)
          : role.value === this.userRole[ROL_DRIVER]
          ? (role.disabled = true)
          : role.value === this.userRole[ROL_USER]
          ? (role.disabled = true)
          : (role.disabled = false)
      }
    },
    changeTheRole(rol) {
      if (
        rol !== ROL_CONTACTO_ID &&
        rol !== ROL_EMERGENCY_CONTACT_ID &&
        rol != ROL_DRIVER_ID &&
        !this.personRol.includes(ROL_USER)
      ) {
        this.personRol.push(ROL_USER)
        this.userRole[ROL_USER] = ROL_USUARIO_ID
        this.userRole[ROL_CONTACT] = ''
        this.profile = ''
        this.selectedThings = []
        this.thingsAdded = []
        this.thingsRemoved = []
        this.hideButtonAddRole = false
      }
      if (
        rol !== ROL_CONTACTO_ID &&
        rol !== ROL_USUARIO_ID &&
        rol != ROL_DRIVER_ID &&
        !this.personRol.includes(ROL_EMERGENCY_CONTACT)
      ) {
        this.personRol.push(ROL_EMERGENCY_CONTACT)
        this.userRole[ROL_EMERGENCY_CONTACT] = ROL_EMERGENCY_CONTACT_ID
        this.userRole[ROL_CONTACT] = ''
        this.hideButtonAddRole = false
      }

      if (rol === ROL_EMERGENCY_CONTACT_ID) {
        this.onErrorMessageForContactEmergencyFields()
        if (this.personPhones.length < 2) {
          this.addContactEmergency()
          this.addContactEmergency()
        }
      } else this.personPhones = []

      if (
        rol !== ROL_CONTACTO_ID &&
        rol !== ROL_USUARIO_ID &&
        rol !== ROL_EMERGENCY_CONTACT_ID &&
        !this.personRol.includes(ROL_DRIVER)
      ) {
        this.personRol.push(ROL_DRIVER)
        this.userRole[ROL_DRIVER] = ROL_DRIVER_ID
        this.userRole[ROL_CONTACT] = ''
        this.hideButtonAddRole = false
      }
    },
    cancel() {
      this.offErrorMessageForContactEmergencyFields()
      this.setPersonInitialValues()
      this.analyzeTheRoleAndProfile()
      this.selectedThings = structuredClone(this.originallySelectedThings)
      this.selectedThingsEmergencyContact = structuredClone(
        this.originalThingsEmergencyContact
      )
      this.thingsRemoved = []
      this.thingsAdded = []
      this.toggleEditable()

      for (const role of this.roleToChoose) {
        role.value === this.userRole[ROL_EMERGENCY_CONTACT]
          ? (role.disabled = true)
          : role.value === this.userRole[ROL_DRIVER]
          ? (role.disabled = true)
          : role.value === this.userRole[ROL_USER]
          ? (role.disabled = true)
          : (role.disabled = false)
      }
    },
    isAuthorizedToEdit() {
      return this.loggedUser === this.person.uriId ? false : true
    },
    analyzeTheRoleAndProfile() {
      const hasUser = this.personRol.find(pr => pr === ROL_USER)

      const hasEmergencyContact = this.personRol.find(
        pr => pr === ROL_EMERGENCY_CONTACT
      )
      const hasDriver = this.personRol.find(pr => pr === ROL_DRIVER)

      if (Boolean(hasUser)) this.userRole[hasUser] = ROL_USUARIO_ID
      else this.userRole[ROL_USER] = ''

      if (Boolean(hasEmergencyContact))
        this.userRole[hasEmergencyContact] = ROL_EMERGENCY_CONTACT_ID
      else this.userRole[ROL_EMERGENCY_CONTACT] = ''

      if (Boolean(hasDriver)) this.userRole[hasDriver] = ROL_DRIVER_ID
      else this.userRole[ROL_DRIVER] = ''

      if (
        Boolean(hasUser) ||
        Boolean(hasEmergencyContact) ||
        Boolean(hasDriver)
      )
        this.userRole[ROL_CONTACT] = ''

      if (
        !Boolean(hasUser) &&
        !Boolean(hasEmergencyContact) &&
        !Boolean(hasDriver)
      ) {
        this.userRole[ROL_CONTACT] = ROL_CONTACTO_ID
        this.userRole[ROL_EMERGENCY_CONTACT] = ''
        this.userRole[ROL_USER] = ''
        this.userRole[ROL_DRIVER] = ''
      }

      if (this.personRol.length > 2) {
        this.roleToChoose = [
          {
            value: 1,
            text: i18n.t(
              'fleet_management.persons.new_person.labels.card_data_role_user'
            ),
            disabled: true
          },
          {
            value: 3,
            text: i18n.t(
              'fleet_management.persons.person_info.labels.card_field_rol_emergency_contact'
            ),
            disabled: true
          },
          {
            value: 4,
            text: i18n.t(
              'fleet_management.persons.person_info.labels.card_field_rol_driver'
            ),
            disabled: true
          }
        ]
      } else if (this.editable) this.initialRoleToChoseValue()

      if (this.personProfile) {
        switch (this.personProfile) {
          case 'business_account_admin':
            this.profile = PROFILE_ADMINISTRADOR_ID
            break
          case 'business_account_user':
            this.profile = PROFILE_MONITOREO_ID
            break
          case 'business_account_fleet_admin':
            this.profile = PROFILE_ADMINISTRADOR_SUBFLEET_ID
            break
        }
      }
    },
    confirmUpdate() {
      if (this.onErrorIdentificationTypeRequired()) return
      this.showDialog = true
      this.dialog.text = i18n.t(
        'fleet_management.persons.person_info.alerts.confirm_update'
      )
      this.dialog.type = 'warning'
    },
    getProfile(rol) {
      // solo los rol user tienen profile
      if (rol === ROL_USER) {
        if (this.person.profile && this.person.profile.length > 0) {
          if (this.personProfile === 'business_account_admin') {
            return i18n.t(
              'fleet_management.persons.person_info.labels.card_field_profile_business_account_admin'
            )
          } else if (this.personProfile === 'business_account_fleet_admin') {
            return i18n.t(
              'fleet_management.persons.person_info.labels.card_field_profile_business_account_admin_subfleet'
            )
          } else {
            return i18n.t(
              'fleet_management.persons.person_info.labels.card_field_profile_business_account_user'
            )
          }
        }
      }
    },
    goBack() {
      this.$router.push({ name: 'PersonsList' })
    },
    toggleEditable() {
      this.editable = !this.editable
      this.valid = false
      this.hideButtonAddRole = false
    },
    updateUser(user) {
      if (!this.isAuthorizedToEdit()) {
        window.localStorage.setItem('user', JSON.stringify(user))
        window.dispatchEvent(
          new CustomEvent('update-people-data', { detail: user })
        )
      }
    },

    async addPersonRole(uriId) {
      const objAddRole = {}

      if (
        this.isContactEmergencyAdded &&
        !this.isUserAdded &&
        !this.isDriverAdded
      )
        objAddRole.phones = this.personPhones

      if (
        !this.isContactEmergencyAdded &&
        this.isUserAdded &&
        !this.isDriverAdded
      ) {
        objAddRole.email = this.email
        objAddRole.profiles = this.personProfile
      }

      if (
        this.isContactEmergencyAdded &&
        this.isUserAdded &&
        !this.isDriverAdded
      ) {
        objAddRole.email = this.email
        objAddRole.profiles = this.personProfile
        objAddRole.phones = this.personPhones
      }

      if (
        this.isContactEmergencyAdded &&
        !this.isUserAdded &&
        this.isDriverAdded
      ) {
        objAddRole.phones = this.personPhones
        objAddRole.ibutton = this.ibutton
      }

      if (
        !this.isContactEmergencyAdded &&
        this.isUserAdded &&
        this.isDriverAdded
      ) {
        objAddRole.email = this.email
        objAddRole.profiles = this.personProfile
        objAddRole.ibutton = this.ibutton
      }

      if (
        !this.isContactEmergencyAdded &&
        !this.isUserAdded &&
        this.isDriverAdded
      ) {
        objAddRole.ibutton = this.ibutton
      }

      if (
        this.isContactEmergencyAdded &&
        this.isUserAdded &&
        this.isDriverAdded
      ) {
        objAddRole.email = this.email
        objAddRole.profiles = this.personProfile
        objAddRole.phones = this.personPhones
        objAddRole.ibutton = this.ibutton
      }

      try {
        const res = await PersonsHelpers.addRolPerson(uriId, objAddRole)
        return res.args[0]
      } catch (e) {
        console.error(e)
      }
    },

    async updateEmergencyContactPhones(uriId) {
      const res = await PersonsHelpers.updatePersonRol(uriId, {
        email: this.email,
        profile: this.personProfile,
        phonesIn: this.isContactEmergencyPhonesUpdated ? this.personPhones : []
      })

      return res.args[0]
    },

    async updateDriverIbutton(uriId) {
      const res = await PersonsHelpers.updatePersonRol(uriId, {
        ibutton: this.ibutton
      })

      return res.args[0]
    },

    async updatePerson() {
      const uriId = this.$route.params.id

      let snackbar = {
        color: '',
        text: ''
      }
      let updatingEmergencyContacts = false
      const firstName =
        this.personFirstName !== this.name ? this.personFirstName : undefined
      const lastName =
        this.personLastName !== this.lastName ? this.personLastName : undefined

      let identifyType = undefined
      let identifyNumber = undefined
      const identifyNumperForStore = PersonsHelpers.preparePersonalDataForStore(
        {
          identificationType: this.personIdentificationType,
          identificationNumber: this.personIdentificationNumber
        }
      )

      if (
        this.personIdentificationType !== this.person?.identification_type ||
        identifyNumperForStore !== this.person?.identification_number ||
        this.isContactEmergencyAdded
      ) {
        identifyType = this.personIdentificationType ?? null
        identifyNumber =
          identifyNumperForStore !== '' ? identifyNumperForStore : null
      }

      this.ibutton = this.personRol.includes(ROL_DRIVER) ? this.ibutton : null

      this.personDataIsUpdated =
        Boolean(firstName) ||
        Boolean(lastName) ||
        Boolean(identifyType) ||
        Boolean(identifyNumber)

      this.loadingThingContactsThingList = true

      this.$store.commit('setNumberCheck', this.checkSingleNumberPhones)

      if (this.checkSingleNumberPhones && this.isContactEmergencyAdded) {
        this.showDialog = false
        this.loadingThingContactsThingList = false
        setTimeout(() => {
          this.$store.commit('setNumberCheck', false)
        }, 5000)
        return
      }

      try {
        let resDataPerson = null

        if (this.isUpdatePerson) {
          const res = await PersonsHelpers.updatePerson(uriId, {
            firstName,
            lastName,
            identifyType,
            identifyNumber,
            phonesIn: this.personPhones
          })
          const personUpdated = res.args[0]

          await store.dispatch('persons/updatePerson', {
            uriId: personUpdated.id,
            firstName: personUpdated.first_name,
            lastName: personUpdated.last_name,
            identifyType: personUpdated.identification_type,
            identifyNumber: personUpdated.identification_number,
            phonesIn: personsHelpers.getPhones(personUpdated),
            ibutton: personsHelpers.getIbutton(personUpdated)
          })

          this.updateUser({
            name: personUpdated.first_name,
            last_name: personUpdated.last_name,
            username: this.email
          })
        }

        if (
          this.isContactEmergencyAdded ||
          this.isUserAdded ||
          this.isDriverAdded
        ) {
          resDataPerson = await this.addPersonRole(
            this.personUpdated?.id ?? uriId
          )
        }

        if (
          this.isContactEmergencyPhonesUpdated &&
          !this.isContactEmergencyAdded &&
          !this.isContactEmergencyRemoved
        ) {
          resDataPerson = await this.updateEmergencyContactPhones(uriId)
        }

        if (
          this.isIbuttonUpdated &&
          !this.isDriverAdded &&
          !this.isDriverRemoved
        ) {
          resDataPerson = await this.updateDriverIbutton(uriId)
        }

        if (this.isProfileChanged) {
          const resData = await PersonsHelpers.updateProfilePerson(uriId, {
            profile: this.personProfile
          })
          resDataPerson = resData.args[0]
        }

        if (this.isUserRemoved) {
          const resData = await PersonsHelpers.removeRolPerson(
            uriId,
            'mrn:party_role:user'
          )
          resDataPerson = resData.args[0]
        }

        if (this.isContactEmergencyRemoved) {
          const resData = await PersonsHelpers.removeRolPerson(
            uriId,
            'mrn:party_role:emergency_contact'
          )
          resDataPerson = resData.args[0]
        }

        if (this.isDriverRemoved) {
          const resData = await PersonsHelpers.removeRolPerson(
            uriId,
            'mrn:party_role:driver'
          )
          resDataPerson = resData.args[0]
        }

        if (this.thingAssignmentsChanged) {
          if (this.thingsAdded.length > 0) {
            await PersonsHelpers.thingAssignmentsAdd(uriId, this.thingsAdded)
            this.originallySelectedThings = structuredClone(this.selectedThings)
          }
          if (this.thingsRemoved.length > 0) {
            await PersonsHelpers.thingAssignmentsRemove(
              uriId,
              this.thingsRemoved
            )
            this.originallySelectedThings = structuredClone(this.selectedThings)
          }
          this.setShowMoreData()
        }

        if (Boolean(resDataPerson)) {
          await store.dispatch('persons/updatePersonRol', {
            roles: personsHelpers.getRoles(resDataPerson),
            profile: personsHelpers.getProfile(resDataPerson),
            phones: personsHelpers.getPhones(resDataPerson),
            ibutton: personsHelpers.getIbutton(resDataPerson)
          })
        }

        this.snack = true
        this.snackColor = 'success'
        this.snackText = i18n.t(
          'fleet_management.persons.person_info.alerts.update_success'
        )
        this.loadingThingContactsThingList = false
        this.showDialog = false
        if (this.editable) {
          this.toggleEditable()
        }
      } catch (error) {
        if (error.kwargs?.code === 'already_exists') {
          this.personDuplicated = true
          this.errorMessage = i18n.t(
            'fleet_management.persons.person_info.errors.mail_match'
          )
        }
        this.snack = true
        this.snackColor = 'error'
        this.snackText = i18n.t(
          'fleet_management.persons.person_info.errors.update_error'
        )
        this.loadingThingContactsThingList = false
        this.showDialog = false
      } finally {
        this.thingsAdded = []
        this.thingsRemoved = []
        this.setPersonInitialValues()
      }

      if (
        (!this.selectedThingsEmergencyContact.length &&
          this.originalThingsEmergencyContact.length) ||
        this.thingsToDelete.length > 0
      ) {
        try {
          this.loadingThingContactsThingList = true
          updatingEmergencyContacts = true
          const thingsListId = this.thingsToDelete.map(t => t.thing_id)
          await ThingsHelpers.eraseThingsFromEmergencyContact(
            uriId,
            this.personFirstName,
            this.personLastName,
            thingsListId
          )
          snackbar = {
            color: 'success',
            text: i18n.t(
              'fleet_management.persons.person_info.alerts.update_success'
            )
          }
          this.editable = false
        } catch (error) {
          console.log(error)
          snackbar = {
            text:
              'Error al borrar los contactos de emergencia. Intente nuevamente.'
          }
        } finally {
          this.loadingThingContactsThingList = false
          updatingEmergencyContacts = false
          this.setPromiseStatus(snackbar, updatingEmergencyContacts)
        }
      }

      if (
        this.selectedThingsEmergencyContact.length > 0 &&
        this.originalThingsEmergencyContact.length > 0 &&
        JSON.stringify(this.selectedThingsEmergencyContact) !==
          JSON.stringify(this.originalThingsEmergencyContact)
      ) {
        try {
          updatingEmergencyContacts = true
          const thingList = this.selectedThingsEmergencyContact.map(
            thing => thing.thing_id
          )
          const response = await ThingsHelpers.createAndUpdateThingsFromEmergencyContact(
            uriId,
            this.personFirstName,
            this.personLastName,
            thingList
          )
          this.updateViewThingContact(response)
          snackbar = {
            color: 'success',
            text: i18n.t(
              'fleet_management.persons.person_info.alerts.update_success'
            )
          }
          this.editable = false
        } catch (error) {
          console.error(error)
          snackbar = {
            text: i18n.t(
              'fleet_management.persons.person_info.errors.error_update_contact_emergency'
            )
          }
        } finally {
          updatingEmergencyContacts = false
          this.setPromiseStatus(snackbar, updatingEmergencyContacts)
        }
      }

      if (
        this.selectedThingsEmergencyContact.length > 0 &&
        !this.originalThingsEmergencyContact.length > 0
      ) {
        try {
          updatingEmergencyContacts = true
          const thingList = this.selectedThingsEmergencyContact.map(
            thing => thing.thing_id
          )
          const response = await ThingsHelpers.createAndUpdateThingsFromEmergencyContact(
            uriId,
            this.personFirstName,
            this.personLastName,
            thingList
          )
          this.updateViewThingContact(response)
        } catch (error) {
          console.error(error)
          snackbar = {
            text: i18n.t(
              'fleet_management.persons.person_info.errors.error_create_contact_emergency'
            )
          }
        } finally {
          updatingEmergencyContacts = false
          this.setPromiseStatus(snackbar, updatingEmergencyContacts)
        }
      }
    },

    updateViewThingContact(response) {
      if (response.length > 0) {
        this.originalThingsEmergencyContact = response.map(thing => {
          const { name, thing_id } = thing
          return {
            thing_id,
            name
          }
        })
        this.selectedThingsEmergencyContact = structuredClone(
          this.originalThingsEmergencyContact
        )
      } else {
        this.originalThingsEmergencyContact = []
      }
    },

    setPromiseStatus(snackbar, updatingEmergencyContacts) {
      if (!updatingEmergencyContacts) {
        this.dialog.loading = false
        this.showDialog = false
        store.dispatch('ui/setSnackbar', snackbar)
        this.editable = false
      }
    },

    setPersonInitialValues() {
      this.personFirstName = this.name
      this.personLastName = this.lastName
      this.personIdentificationNumber = this.person?.identification_number
      this.personIdentificationType = this.person?.identification_type
      this.personProfile = this.getPersonProfile
      this.personRol = structuredClone(this.getPersonRole)
      this.personPhones = structuredClone(this.getPhones)
      this.ibutton = this.person?.ibutton
    },

    formatDataThings(things) {
      if (Boolean(things))
        return things.map(thing => {
          return {
            uriId: thing.uriId ?? thing.id,
            name: thing.name ?? thing.info.label
          }
        })

      return []
    },
    setShowMoreData() {
      const endIdx = 10 % this.selectedThings.length
      this.selectedThingShowed =
        this.selectedThings.length > 10
          ? structuredClone(this.selectedThings).splice(0, endIdx)
          : structuredClone(this.selectedThings)

      this.textShowMore = `${this.selectedThings.length} vehículos en total`
    },
    async getThingAssignments() {
      try {
        const personId = this.$route.params.id
        const data = await PersonsHelpers.thingAssignmentsGet(personId)
        this.selectedThings = data.map(thing => {
          return { uriId: thing.id, name: thing.info.label }
        })

        this.setShowMoreData()

        this.originallySelectedThings = structuredClone(this.selectedThings)
      } catch (e) {
        this.snack = true
        this.snackColor = 'error'
        this.snackText = i18n.t(
          'fleet_management.persons.person_info.errors.error_update_dates_entities'
        )
      }
    },
    getThingContactEmergencyAssignments() {
      this.dataThingContactEmergencyAssignments = structuredClone(
        this.selectedThingsEmergencyContact
      )
    },
    async getSelectedLimitedThings() {
      try {
        if (!Boolean(this.getThings.length)) {
          await this.$store.dispatch('things/getThingsList')
        }
      } catch (e) {
        this.snack = true
        this.snackColor = 'error'
        this.snackText = i18n.t(
          'fleet_management.persons.person_info.errors.error_loading_data'
        )
      }
    },

    isBusinessAccountUser(rol) {
      return (
        (this.personProfile === 'business_account_user' ||
          this.personProfile === 'business_account_fleet_admin') &&
        rol !== ROL_EMERGENCY_CONTACT &&
        rol !== ROL_DRIVER
      )
    },
    selectAll() {
      if (
        this.selectedThings.length ===
        this.filteredThingsByLiveTrackingService.length
      ) {
        this.selectedThings = []
      } else {
        this.selectedThings = this.formatDataThings(
          this.filteredThingsByLiveTrackingService
        )
      }
    },
    selectAllThingsContactEmergency() {
      if (
        this.selectedThingsEmergencyContact.length ===
        this.filteredThingsByLiveTrackingService.length
      ) {
        this.selectedThingsEmergencyContact = []
      } else {
        this.selectedThingsEmergencyContact = structuredClone(
          this.filteredThingsByLiveTrackingService
        )
      }
    },
    async personDetailFetch() {
      const userId = this.$route.params.id
      try {
        const data = await PersonsHelpers.personFetch({ userId })
        const personData = data.args[0]
        store.dispatch('persons/setResponseError', false)
        const person = structuredClone(personData)
        this.createdBy = person.created_by
        person.phones = PersonsHelpers.getPhones(personData)
        person.profile = PersonsHelpers.getProfile(personData)
        person.roles = PersonsHelpers.getRoles(personData)
        person.ibutton = PersonsHelpers.getIbutton(personData)
        store.dispatch('persons/setCurrentPerson', person)
      } catch (err) {
        console.log(err)
        store.dispatch('persons/setResponseError', true)
        if (err.error == 'com.magenta.error.operation_not_allowed') {
          const response = i18n.t(
            'fleet_management.persons.persons_tab_all.errors.no_permission'
          )
          store.dispatch('persons/setResponseMessage', response)
        } else if (err.error == 'com.magenta.error.not_found') {
          return true
        } else {
          const response = i18n.t(
            'fleet_management.persons.persons_tab_all.errors.other_error'
          )
          store.dispatch('persons/setResponseMessage', response)
        }
        console.error(err)
      }
    },
    getThingContactsThingList() {
      const accountId = this.person.account_id
      this.loadingThingContactsThingList = true
      ThingsHelpers.getThingContactsThingList(accountId, this.$route.params.id)
        .then(response => {
          const thingIds = response.args[0].map(thing => thing.thing_id)
          const arr = thingIds
            .map(thing_id => {
              const thing = this.getThings.find(item => item.uriId === thing_id)
              return {
                thing_id: thing_id,
                name: thing ? thing.name : ''
              }
            })
            .filter(item => item.name)

          this.originalThingsEmergencyContact = arr
          this.selectedThingsEmergencyContact = structuredClone(
            this.originalThingsEmergencyContact
          )
        })
        .catch(error => {
          if (error.error !== 'com.magenta.error.not_found') {
            const errorText = ThingsHelpers.getErrorMessageByCode(error.error)
            const snackbar = {
              color: 'error',
              text: errorText
            }
            store.dispatch('ui/setSnackbar', snackbar)
          }
        })
        .finally(() => {
          this.loadingThingContactsThingList = false
        })
    }
  },
  computed: {
    ...mapGetters('persons', [
      'getThingsAssignments',
      'getPersonProfile',
      'getPersonRole',
      'getPhones'
    ]),
    ...mapGetters('auth', ['session']),
    ...mapGetters('things', [
      'getThings',
      'getContactEmergencyFromPerson',
      'filteredThingsByLiveTrackingService'
    ]),
    notDisplayDropdownThingsForAdsfAndMonitoring() {
      const profile = this.personProfile
      const noEntities = this.selectedThings.length === 0

      if (
        (profile === 'business_account_fleet_admin' ||
          profile === 'business_account_user') &&
        noEntities
      ) {
        return false
      }

      return true
    },
    showThingsLoader() {
      return this.loadingAssignedThings || this.loadingThings
    },
    isAdmin() {
      return store.getters.isAdmin
    },
    isAdminSubFleet() {
      return store.getters.isAdminSubFleet
    },
    isNewPersonView() {
      return this.$route.name === 'NewPerson'
    },
    selectedSomeThings() {
      return this.selectedThings.length > 0
    },
    selectedAllThings() {
      this.selectedThingsEmergencyContact.length ===
        this.allSelectedThingsFiltered.length
    },
    selectedSomeThingsEmergency() {
      return (
        this.selectedThingsEmergencyContact.length > 0 &&
        !this.selectedAllThings
      )
    },
    selectedAllThingsContactEmergency() {
      return (
        this.selectedThingsEmergencyContact.length ===
        this.allThingsFiltered.length
      )
    },
    selectedSomeEntitiesContactEmergency() {
      return (
        this.selectedThingsEmergencyContact.length > 0 &&
        !this.selectedAllThingsContactEmergency.length
      )
    },
    icon() {
      if (this.selectedSomeThings) return 'mdi-minus-box'
      if (this.selectedSomeThingsEmergency) return 'mdi-close-box'
      return 'mdi-checkbox-blank-outline'
    },
    iconContactEmergency() {
      if (this.selectedAllThingsContactEmergency) return 'mdi-minus-box'
      if (this.selectedSomeEntitiesContactEmergency) return 'mdi-close-box'
      return 'mdi-checkbox-blank-outline'
    },

    thingAssignmentsChanged() {
      return (
        JSON.stringify(ThingsHelpers.sortEntities(this.selectedThings)) !==
        JSON.stringify(
          ThingsHelpers.sortEntities(this.originallySelectedThings)
        )
      )
    },

    emergencyContactsThingAssignmentsChanged() {
      return (
        JSON.stringify(
          ThingsHelpers.sortEntities(this.selectedThingsEmergencyContact)
        ) !==
        JSON.stringify(
          ThingsHelpers.sortEntities(this.originalThingsEmergencyContact)
        )
      )
    },

    isIbuttonUpdated() {
      const hasDriver = this.personRol.includes(ROL_DRIVER)
      return hasDriver && this.ibutton != null
        ? this.ibutton !== this.person?.ibutton
        : false
    },

    isContactEmergencyPhonesUpdated() {
      const formattedPhones = structuredClone(this.personPhones).map(
        element => {
          return {
            index: element.index,
            phone: {
              number: element.phone.number,
              tag: element.phone.tag
            }
          }
        }
      )
      return JSON.stringify(formattedPhones) !== JSON.stringify(this.getPhones)
    },
    isContactEmergencyAdded() {
      return (
        this.personRol.includes(ROL_EMERGENCY_CONTACT) &&
        !this.getPersonRole.includes(ROL_EMERGENCY_CONTACT)
      )
    },
    isContactEmergencyRemoved() {
      return (
        !this.personRol.includes(ROL_EMERGENCY_CONTACT) &&
        this.getPersonRole.includes(ROL_EMERGENCY_CONTACT)
      )
    },
    isUserAdded() {
      return (
        this.personRol.includes(ROL_USER) &&
        !this.getPersonRole.includes(ROL_USER)
      )
    },
    isUserRemoved() {
      return (
        !this.personRol.includes(ROL_USER) &&
        this.getPersonRole.includes(ROL_USER)
      )
    },
    isDriverAdded() {
      return (
        this.personRol.includes(ROL_DRIVER) &&
        !this.getPersonRole.includes(ROL_DRIVER)
      )
    },
    isDriverRemoved() {
      return (
        !this.personRol.includes(ROL_DRIVER) &&
        this.getPersonRole.includes(ROL_DRIVER)
      )
    },
    isProfileChanged() {
      return (
        this.personProfile !== this.getPersonProfile &&
        this.getPersonRole.includes(ROL_USER)
      )
    },
    isContactEmergency() {
      return this.personRol.includes(ROL_EMERGENCY_CONTACT)
    },
    checkSingleNumberPhones() {
      const itemCount = {}

      this.personPhones.forEach(value => {
        const number = value.phone.number
        number in itemCount
          ? (itemCount[number] = itemCount[number] + 1)
          : (itemCount[number] = 1)
      })

      const check = Object.values(itemCount).some(el => el > 1)

      return check
    },
    formPersonalDataHasBeenChanged() {
      return (
        this.personFirstName !== this.name ||
        this.personLastName !== this.lastName ||
        PersonsHelpers.preparePersonalDataForStore({
          identificationType: this.personIdentificationType,
          identificationNumber: this.personIdentificationNumber
        }) !== this.person.identification_number ||
        this.personIdentificationType !== this.person.identification_type ||
        this.ibutton !== this.person?.ibutton
      )
    },
    isUpdatePerson() {
      return (
        this.personDataIsUpdated ||
        this.isContactEmergencyAdded ||
        this.isContactEmergencyPhonesUpdated
      )
    },
    formHasNotChanged() {
      let value = true
      if (
        this.formPersonalDataHasBeenChanged ||
        this.personProfile !== this.getPersonProfile ||
        this.isContactEmergencyPhonesUpdated ||
        JSON.stringify(this.personRol) !== JSON.stringify(this.getPersonRole) ||
        this.thingAssignmentsChanged ||
        this.emergencyContactsThingAssignmentsChanged
      ) {
        value = false
      }
      return value
    },
    isUserAdmin() {
      const isAdmin = store.getters.isAdmin
      return isAdmin
    },
    formatDataThingsEmergencyContact() {
      const selectedThingsName = []
      for (
        let i = 0;
        i < this.filteredThingsByLiveTrackingService.length;
        i++
      ) {
        selectedThingsName.push({
          thing_id: this.filteredThingsByLiveTrackingService[i].uriId,
          name: this.filteredThingsByLiveTrackingService[i].name
        })
      }
      return selectedThingsName
    },
    ...mapState('persons', {
      name: state => state.currentPerson.first_name,
      lastName: state => state.currentPerson.last_name,
      email: state => state.currentPerson.emails[0].email,
      person: state => state.currentPerson
    })
  },
  watch: {
    personIdentificationNumber() {
      this.personDuplicated = false
      this.errorMessage = ''
      this.offErrorMessageForContactEmergencyFields()
    },
    personIdentificationType() {
      this.offErrorMessageForContactEmergencyFields()
    },
    selectedThings: {
      deep: true,
      handler(newValue, oldValue) {
        if (!this.loadingAssignedThings) {
          if (newValue.length === this.getThings.length) {
            this.thingsAdded = structuredClone(newValue)
            return
          }
          const newData = newValue[newValue.length - 1]
          if (newValue.length > oldValue.length) {
            if (!oldValue.some(thing => thing.uriId === newData.uriId)) {
              this.thingsAdded.push(newData)
              this.thingsAdded = [...new Set(this.thingsAdded)]
            }
          } else {
            if (newValue.length < oldValue.length)
              this.thingsRemoved.push(
                ...oldValue.filter(
                  thingA =>
                    !newValue.some(thingB => thingB.uriId === thingA.uriId)
                )
              )
            this.thingsRemoved = [...new Set(this.thingsRemoved)]
          }
        }
      }
    },
    async session(newSession) {
      this.loadingThings = true
      if (newSession.isOpen) {
        await this.personDetailFetch()
        this.loadingPerson = false
        this.setPersonInitialValues()
        this.analyzeTheRoleAndProfile()
        await this.getThingAssignments()
        this.loadingAssignedThings = false
        await this.getSelectedLimitedThings()
        this.loadingThings = false
        await this.getPersonsList()
        if (this.isAdmin) {
          this.getThingContactsThingList()
        }
      }
    },
    selectedThingsEmergencyContact: {
      handler(newVal) {
        this.thingsToDelete = this.originalThingsEmergencyContact.filter(
          originalThing =>
            !newVal.find(
              selected => selected.thing_id === originalThing.thing_id
            )
        )
      },
      deep: true
    }
  },
  async created() {
    if (this.session.isOpen) {
      await this.personDetailFetch()
      this.loadingPerson = false
      this.setPersonInitialValues()
      this.analyzeTheRoleAndProfile()
      await this.getThingAssignments()
      this.loadingAssignedThings = false
      if (this.isAdmin) {
        this.getThingContactsThingList()
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.person__info__pre-header {
  border-bottom: 1px solid var(--pale-sky-blue);
}
.service-columns {
  min-width: 20%;
  max-width: 20%;
}
.circle-chip {
  width: 11px;
  height: 11px;
  background-color: #ff605b;
  border-radius: 50%;
}
.divider-dotted {
  border-width: 0;
  background-size: 6px 3px;
  background-repeat: repeat-x;
  background-position-y: bottom;
  background-image: radial-gradient(
    circle,
    rgba(0, 0, 0, 0.15) 0.05rem,
    transparent 1.5px
  );
  height: 3px;
}
</style>
